import { useState, useMemo, useCallback, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';

// ─── Helpers ────────────────────────────────────────────────────

const formatCurrency = (amount: number): string =>
	'$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
const GREEN_GRADIENT = 'linear-gradient(135deg, #059669, #10b981)';

const TIP_OPTIONS = [10, 15, 20, 25] as const;
const FEE_RATE = 0.03;
const FEE_FLAT = 0.15;

// ─── Static Invoice Data (will come from API later) ─────────────

const MOCK_INVOICE = {
	id: 'INV-0023-2025',
	salonName: 'Glamour Locks Braiding Studio',
	clientName: 'Monica Davis',
	serviceName: 'Knotless Braids',
	serviceSize: 'Medium',
	hairLength: 'Hip',
	appointmentDate: 'Sat, Feb 15, 2025',
	appointmentTime: '10:00 AM',
	stylistName: 'Sarah Johnson',
	servicePrice: 220.0,
	depositPaid: 50.0,
	discount: 15.0,
	miscLabel: 'Hair extensions',
	miscAmount: 25.0,
	balanceDue: 180.0,
};

// ─── Payment Method Config ──────────────────────────────────────

interface PaymentMethod {
	key: string;
	name: string;
	desc: string;
	icon: string;
	iconBg: string;
}

const PAYMENT_METHODS: PaymentMethod[] = [
	{
		key: 'card',
		name: 'Credit / Debit Card',
		desc: 'Visa, Mastercard, Amex',
		icon: '\uD83D\uDCB3',
		iconBg: GRADIENT,
	},
	{
		key: 'apple',
		name: 'Apple Pay',
		desc: 'Quick & secure payment',
		icon: '\uF8FF',
		iconBg: '#2d2240',
	},
	{
		key: 'paypal',
		name: 'PayPal',
		desc: 'Pay with your PayPal account',
		icon: 'PP',
		iconBg: '#003087',
	},
];

// ─── Main Component ─────────────────────────────────────────────

const InvoicePay = () => {
	const { token } = useParams<{ token: string }>();

	// State
	const [selectedTipPct, setSelectedTipPct] = useState<number | null>(20);
	const [customTip, setCustomTip] = useState('');
	const [showConfirm, setShowConfirm] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);
	const [selectedMethod, setSelectedMethod] = useState('');

	// Use token in future API calls
	void token;

	const invoice = MOCK_INVOICE;

	// Calculations
	const tipAmount = useMemo(() => {
		if (customTip !== '') return parseFloat(customTip) || 0;
		if (selectedTipPct !== null) return invoice.balanceDue * (selectedTipPct / 100);
		return 0;
	}, [selectedTipPct, customTip, invoice.balanceDue]);

	const fee = useMemo(
		() => (invoice.balanceDue + tipAmount) * FEE_RATE + FEE_FLAT,
		[invoice.balanceDue, tipAmount]
	);

	const total = useMemo(
		() => invoice.balanceDue + tipAmount + fee,
		[invoice.balanceDue, tipAmount, fee]
	);

	// Handlers
	const handleTipSelect = useCallback((pct: number) => {
		setCustomTip('');
		setSelectedTipPct(pct);
	}, []);

	const handleCustomTip = useCallback((value: string) => {
		setSelectedTipPct(null);
		setCustomTip(value);
	}, []);

	const handlePaymentSelect = useCallback((method: string) => {
		setSelectedMethod(method);
		setShowConfirm(true);
	}, []);

	const handleConfirmPay = useCallback(() => {
		setShowConfirm(false);
		setTimeout(() => setShowSuccess(true), 200);
	}, []);

	const breakdown = useMemo(() => {
		let text = formatCurrency(invoice.balanceDue);
		if (tipAmount > 0) text += ` + ${formatCurrency(tipAmount)} tip`;
		text += ` + ${formatCurrency(fee)} fee`;
		return text;
	}, [invoice.balanceDue, tipAmount, fee]);

	const methodIcon: Record<string, string> = {
		'Credit / Debit Card': '\uD83D\uDCB3',
		'Apple Pay': '\uF8FF',
		PayPal: 'PP',
		'Pay at Salon': '\uD83D\uDCB5',
	};

	return (
		<div
			className="min-h-screen"
			style={{ background: '#f0eef5', fontFamily: "'DM Sans', sans-serif", color: '#2d2240' }}
		>
			{/* Top Bar */}
			<div className="text-center py-4 px-6" style={{ background: GRADIENT }}>
				<div className="text-base font-bold text-white">{invoice.salonName}</div>
				<div
					className="text-[10px] font-semibold uppercase tracking-[2px] mt-0.5"
					style={{ color: '#c9a84c' }}
				>
					200Below Network
				</div>
			</div>

			{/* Container */}
			<div className="max-w-[520px] mx-auto px-4 pb-10">
				{/* Invoice Card */}
				<div
					className="bg-white rounded-2xl overflow-hidden -mt-px"
					style={{ boxShadow: '0 4px 24px rgba(45,34,64,0.08)' }}
				>
					{/* Invoice Header */}
					<div className="text-center py-7 px-7" style={{ background: GRADIENT }}>
						<div
							className="text-[11px] font-semibold uppercase tracking-[2px]"
							style={{ color: 'rgba(255,255,255,0.6)' }}
						>
							Balance Due
						</div>
						<div className="text-[42px] font-extrabold text-white mt-1 leading-none">
							{formatCurrency(invoice.balanceDue)}
						</div>
						<div
							className="inline-block mt-2.5 px-3.5 py-1 rounded-2xl text-xs font-semibold font-mono tracking-wide"
							style={{
								background: 'rgba(255,255,255,0.15)',
								color: 'rgba(255,255,255,0.85)',
							}}
						>
							{invoice.id}
						</div>
					</div>

					{/* Invoice Body */}
					<div className="p-5 px-7">
						<InvoiceRow label="Client" value={invoice.clientName} />
						<InvoiceRow
							label="Service"
							value={`${invoice.serviceName} \u2022 ${invoice.serviceSize} \u2022 ${invoice.hairLength}`}
						/>
						<InvoiceRow
							label="Date"
							value={`${invoice.appointmentDate} \u2022 ${invoice.appointmentTime}`}
						/>
						<InvoiceRow label="Stylist" value={invoice.stylistName} />

						<div className="h-px my-1" style={{ background: '#f0ecf5' }} />

						<InvoiceRow
							label="Service Price"
							value={formatCurrency(invoice.servicePrice)}
						/>
						<InvoiceRow
							label="Deposit Paid"
							value={`-${formatCurrency(invoice.depositPaid)}`}
							valueColor="#10b981"
						/>
						<InvoiceRow
							label="Discount"
							value={`-${formatCurrency(invoice.discount)}`}
							valueColor="#c9a84c"
						/>
						<InvoiceRow
							label={`Misc (${invoice.miscLabel})`}
							value={`+${formatCurrency(invoice.miscAmount)}`}
							valueColor="#764ba2"
						/>

						<div className="h-px my-1" style={{ background: '#f0ecf5' }} />

						<div className="flex justify-between items-center py-2 text-sm">
							<span className="font-bold" style={{ color: '#2d2240' }}>
								Balance Due
							</span>
							<span className="text-lg font-extrabold" style={{ color: '#764ba2' }}>
								{formatCurrency(invoice.balanceDue)}
							</span>
						</div>
					</div>
				</div>

				{/* Tip Section */}
				<SectionTitle>Add a Tip</SectionTitle>
				<div
					className="bg-white rounded-2xl p-6"
					style={{ boxShadow: '0 2px 12px rgba(45,34,64,0.06)' }}
				>
					<p className="text-[13px] text-center mb-4" style={{ color: '#6b5f82' }}>
						Show your stylist some love
					</p>

					{/* Tip Grid */}
					<div className="grid grid-cols-4 gap-2 mb-4">
						{TIP_OPTIONS.map(pct => {
							const amt = invoice.balanceDue * (pct / 100);
							const isActive = selectedTipPct === pct && customTip === '';
							return (
								<button
									key={pct}
									onClick={() => handleTipSelect(pct)}
									className="py-3.5 rounded-xl text-center transition-all cursor-pointer"
									style={{
										border: `2px solid ${isActive ? '#764ba2' : '#e8e0f0'}`,
										background: isActive ? GRADIENT : '#fff',
									}}
								>
									<div
										className="text-base font-bold"
										style={{ color: isActive ? '#fff' : '#764ba2' }}
									>
										{pct}%
									</div>
									<div
										className="text-[11px] mt-0.5"
										style={{
											color: isActive ? 'rgba(255,255,255,0.75)' : '#9b8fb8',
										}}
									>
										{formatCurrency(amt)}
									</div>
								</button>
							);
						})}
					</div>

					{/* Custom Tip */}
					<div className="flex items-center justify-center gap-2 mb-4">
						<span className="text-[13px]" style={{ color: '#9b8fb8' }}>
							or enter custom:
						</span>
						<div
							className="flex items-center gap-0.5 px-3 py-1.5 rounded-[10px]"
							style={{ border: '2px solid #e8e0f0', background: '#fff' }}
						>
							<span className="text-sm font-bold" style={{ color: '#764ba2' }}>
								$
							</span>
							<input
								type="number"
								value={customTip}
								onChange={e => handleCustomTip(e.target.value)}
								placeholder="0.00"
								min="0"
								step="0.01"
								className="w-[60px] border-none outline-none text-sm font-semibold bg-transparent"
								style={{ color: '#2d2240', fontFamily: 'inherit' }}
							/>
						</div>
					</div>

					{/* Tip Summary */}
					<div
						className="flex justify-between items-center pt-3.5 text-[13px]"
						style={{ borderTop: '1px solid #f0ecf5', color: '#6b5f82' }}
					>
						<span>
							Tip:{' '}
							<strong style={{ color: '#764ba2' }}>
								{formatCurrency(tipAmount)}
							</strong>
						</span>
						<span>
							<span className="font-semibold">Total: </span>
							<span
								className="text-[15px] font-extrabold"
								style={{ color: '#2d2240' }}
							>
								{formatCurrency(total)}
							</span>
						</span>
					</div>

					{/* Fee Note */}
					<div
						className="flex items-center justify-between rounded-[10px] p-3 px-4 mt-3.5 text-xs"
						style={{
							background: '#fafafa',
							border: '1px solid #f0ecf5',
							color: '#9b8fb8',
						}}
					>
						<span>
							Transaction Fee{' '}
							<span
								className="font-semibold px-2 py-0.5 rounded ml-1"
								style={{
									background: '#fffbf0',
									color: '#c9a84c',
									fontSize: '11px',
								}}
							>
								3% + $0.15
							</span>
						</span>
						<span className="font-bold" style={{ color: '#6b5f82' }}>
							+{formatCurrency(fee)}
						</span>
					</div>
				</div>

				{/* Payment Methods */}
				<SectionTitle>Choose Payment Method</SectionTitle>
				<div
					className="bg-white rounded-2xl p-2"
					style={{ boxShadow: '0 2px 12px rgba(45,34,64,0.06)' }}
				>
					{PAYMENT_METHODS.map(method => (
						<button
							key={method.key}
							onClick={() => handlePaymentSelect(method.name)}
							className="flex items-center gap-3.5 w-full py-4 px-[18px] rounded-xl cursor-pointer transition-all text-left"
							style={{ border: '2px solid transparent' }}
							onMouseEnter={e => {
								e.currentTarget.style.background = '#f7f5fb';
								e.currentTarget.style.borderColor = '#ede8f5';
							}}
							onMouseLeave={e => {
								e.currentTarget.style.background = 'transparent';
								e.currentTarget.style.borderColor = 'transparent';
							}}
						>
							<div
								className="w-12 h-12 rounded-xl flex items-center justify-center text-[22px] text-white shrink-0"
								style={{
									background: method.iconBg,
									fontWeight: method.key === 'paypal' ? 700 : 400,
									fontSize: method.key === 'paypal' ? '14px' : '22px',
								}}
							>
								{method.icon}
							</div>
							<div className="flex-1">
								<div className="text-[15px] font-bold" style={{ color: '#2d2240' }}>
									{method.name}
								</div>
								<div className="text-xs mt-0.5" style={{ color: '#9b8fb8' }}>
									{method.desc}
								</div>
							</div>
							<div className="text-xl font-light" style={{ color: '#d8d0e4' }}>
								&#8250;
							</div>
						</button>
					))}
				</div>

				{/* Or Divider */}
				<div className="flex items-center gap-4 my-5 px-1">
					<div className="flex-1 h-px" style={{ background: '#e8e0f0' }} />
					<span
						className="text-xs font-semibold uppercase tracking-[1px]"
						style={{ color: '#9b8fb8' }}
					>
						or
					</span>
					<div className="flex-1 h-px" style={{ background: '#e8e0f0' }} />
				</div>

				{/* Pay at Salon */}
				<button
					onClick={() => handlePaymentSelect('Pay at Salon')}
					className="block w-full text-center py-3.5 text-sm font-bold rounded-xl cursor-pointer transition-all"
					style={{ color: '#764ba2', border: '2px solid #e8e0f0', background: '#fff' }}
					onMouseEnter={e => {
						e.currentTarget.style.borderColor = '#764ba2';
						e.currentTarget.style.background = '#f7f5fb';
					}}
					onMouseLeave={e => {
						e.currentTarget.style.borderColor = '#e8e0f0';
						e.currentTarget.style.background = '#fff';
					}}
				>
					Pay at Salon &mdash; Cash or card upon arrival
				</button>

				{/* Secure Badge */}
				<div
					className="flex items-center justify-center gap-1.5 mt-5 pb-3 text-xs"
					style={{ color: '#9b8fb8' }}
				>
					<ShieldCheckIcon className="w-3.5 h-3.5" style={{ fill: '#9b8fb8' }} />
					Secure, encrypted payment
				</div>
			</div>

			{/* Confirm Modal */}
			{showConfirm && (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center transition-all"
					style={{ background: 'rgba(45,34,64,0.6)' }}
					onClick={e => {
						if (e.target === e.currentTarget) setShowConfirm(false);
					}}
				>
					<div className="bg-white max-w-[400px] w-[90%] rounded-[20px] overflow-hidden animate-scale-in">
						<div
							className="text-center pt-7 pb-[22px] px-6 text-white"
							style={{ background: GRADIENT }}
						>
							<div className="text-4xl mb-1.5">
								{methodIcon[selectedMethod] || '\uD83D\uDCB3'}
							</div>
							<div className="text-lg font-bold">Confirm Payment</div>
							<div className="text-[13px] opacity-80 mt-1">via {selectedMethod}</div>
						</div>
						<div className="p-7 text-center">
							<div
								className="text-[32px] font-extrabold"
								style={{ color: '#764ba2' }}
							>
								{formatCurrency(total)}
							</div>
							<div className="text-[13px] mt-1 mb-6" style={{ color: '#6b5f82' }}>
								{breakdown}
							</div>
							<div className="flex gap-2.5">
								<button
									onClick={() => setShowConfirm(false)}
									className="flex-1 py-3.5 rounded-xl text-[15px] font-bold cursor-pointer border-none transition-all"
									style={{
										background: '#f0eef5',
										color: '#6b5f82',
										fontFamily: 'inherit',
									}}
									onMouseEnter={e =>
										(e.currentTarget.style.background = '#e8e0f0')
									}
									onMouseLeave={e =>
										(e.currentTarget.style.background = '#f0eef5')
									}
								>
									Cancel
								</button>
								<button
									onClick={handleConfirmPay}
									className="flex-1 py-3.5 rounded-xl text-[15px] font-bold cursor-pointer border-none text-white transition-all"
									style={{ background: GRADIENT, fontFamily: 'inherit' }}
									onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
									onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
								>
									{selectedMethod === 'Pay at Salon'
										? 'Confirm'
										: 'Confirm & Pay'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Success Modal */}
			{showSuccess && (
				<div
					className="fixed inset-0 z-[200] flex items-center justify-center transition-all"
					style={{ background: 'rgba(45,34,64,0.6)' }}
				>
					<div className="bg-white max-w-[400px] w-[90%] rounded-[20px] overflow-hidden text-center animate-scale-in">
						<div
							className="pt-9 pb-7 px-6 text-white"
							style={{ background: GREEN_GRADIENT }}
						>
							<div
								className="w-16 h-16 rounded-full inline-flex items-center justify-center text-[32px] mb-3"
								style={{ background: 'rgba(255,255,255,0.2)' }}
							>
								&#10003;
							</div>
							<div className="text-xl font-extrabold">Payment Successful!</div>
							<div className="text-[13px] opacity-85 mt-1">
								Your stylist has been notified
							</div>
						</div>
						<div className="p-7">
							<div className="text-4xl font-extrabold" style={{ color: '#059669' }}>
								{formatCurrency(total)}
							</div>
							<div className="text-sm mt-1 mb-2" style={{ color: '#6b5f82' }}>
								via {selectedMethod}
							</div>
							<div
								className="text-xs px-3.5 py-1.5 rounded-lg inline-block font-mono"
								style={{ color: '#9b8fb8', background: '#f0eef5' }}
							>
								REF: BBP-20250215-0023
							</div>
						</div>
						<button
							onClick={() => window.close()}
							className="block w-full py-4 text-base font-bold text-white cursor-pointer border-none mt-6"
							style={{
								background: GREEN_GRADIENT,
								fontFamily: 'inherit',
								borderRadius: '0 0 20px 20px',
							}}
							onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
							onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
						>
							Done &#10003;
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

// ─── Sub-components ─────────────────────────────────────────────

const InvoiceRow = ({
	label,
	value,
	valueColor,
}: {
	label: string;
	value: string;
	valueColor?: string;
}) => (
	<div className="flex justify-between items-center py-2 text-sm">
		<span style={{ color: '#6b5f82' }}>{label}</span>
		<span className="font-semibold" style={{ color: valueColor || '#2d2240' }}>
			{value}
		</span>
	</div>
);

const SectionTitle = ({ children }: { children: ReactNode }) => (
	<div
		className="text-[11px] font-bold uppercase tracking-[2px] mt-7 mb-3.5 px-1"
		style={{ color: '#c9a84c' }}
	>
		{children}
	</div>
);

export default InvoicePay;
